import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";

export interface LinkedInChatByContactParams {
  contactId: string;
  channel?: string;
  accountId?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useLinkedInChatByContact(
  params: Ref<LinkedInChatByContactParams | null> | LinkedInChatByContactParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["linkedin-chat-by-contact", "disabled"];
    return ["linkedin-chat-by-contact", p.contactId, p.accountId || "", p.channel || "linkedin"];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.contactId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const response = await Unipile.getChatByContact(
        p.contactId,
        p.channel || "linkedin",
        p.accountId
      );

      if (response.data?.status) {
        return response.data.data;
      }

      throw new Error("Failed to load LinkedIn chat by contact");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
